/* The one way an unknown thrown value becomes a sentence on screen.
 *
 * Ten call sites wrote `e instanceof Error ? e.message : String(e)`, and it
 * was wrong in the same way in all ten: supabase-js rejects with a
 * PostgrestError / AuthError, which is a plain object carrying `message`, not
 * an `Error` instance — so the deployed admin screen greeted its administrator
 * with "[object Object]" in a red banner (seen live, 11 Sep). Anything with a
 * string `message` is a message; anything else is described, never
 * stringified into its constructor name. */
export function messageOf(e: unknown): string {
  // 176. While viewing as somebody every write is refused read-only (25006).
  // Said once, here, so no screen shows Postgres's sentence instead.
  if (e && typeof e === 'object' && (e as { code?: unknown }).code === '25006') {
    return 'You are viewing as someone else, so nothing can be changed. Stop viewing to make changes.'
  }
  if (e instanceof Error) return e.message
  if (typeof e === 'string') return e
  if (e && typeof e === 'object') {
    const m = (e as { message?: unknown }).message
    if (typeof m === 'string' && m.trim()) return m
    const d = (e as { details?: unknown }).details
    if (typeof d === 'string' && d.trim()) return d
  }
  return 'Something went wrong, and the error carried no message.'
}

/**
 * An Edge Function's own sentence, not supabase-js's (12 Oct). A non-2xx from
 * functions.invoke() arrives as FunctionsHttpError whose message is always
 * "Edge Function returned a non-2xx status code"; the function's reason is in
 * the response body ({ error }), on `context`. David saw exactly that line in
 * the composer when create_post had refused with a real reason. Returns an
 * Error carrying the body's words when there are any, else the original error.
 */
export async function functionError(e: unknown): Promise<unknown> {
  const ctx = (e as { context?: Response } | null)?.context
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = (await ctx.clone().json()) as { error?: unknown }
      if (typeof body?.error === 'string' && body.error.trim()) return new Error(body.error)
    } catch {
      /* Not JSON: keep the original error. */
    }
  }
  return e
}
